import React, { useState, useRef } from 'react';
import axios from 'axios';
import { Upload, X, Link as LinkIcon, Check, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { API } from '../config';

/**
 * Reusable native image upload field for admin forms.
 * Upload-first UX: big click target opens the device file picker (gallery on
 * mobile, file explorer on desktop). Manual URL paste lives under an Advanced
 * toggle (still functional but de-emphasised).
 */
export const ImageUpload = ({ value, onChange, label = 'Image', testId, required = false, userMode = false }) => {
  const [uploading, setUploading] = useState(false);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const fileRef = useRef(null);

  const uploadFile = async (file) => {
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      toast.error('Only image files are supported');
      return;
    }
    if (file.size > 20 * 1024 * 1024) {
      toast.error('File too large (max 20MB)');
      return;
    }
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const endpoint = userMode ? `${API}/uploads/payment-proof` : `${API}/uploads/image`;
      const { data } = await axios.post(endpoint, formData, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      onChange(data.url);
      toast.success('Image uploaded');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const onPickFile = (e) => uploadFile(e.target.files?.[0]);
  const onDrop = (e) => {
    e.preventDefault();
    setDragActive(false);
    uploadFile(e.dataTransfer.files?.[0]);
  };
  const onDragOver = (e) => { e.preventDefault(); setDragActive(true); };
  const onDragLeave = () => setDragActive(false);
  const triggerPicker = () => fileRef.current?.click();
  const clearImage = () => onChange('');

  return (
    <div className="space-y-2" data-testid={testId}>
      {label && (
        <label className="block text-sm" style={{ color: '#A3B8AC' }}>
          {label}{required && <span style={{ color: '#EF4444' }}> *</span>}
        </label>
      )}

      {/* Hidden native input (gallery on mobile, file explorer on desktop) */}
      <input
        ref={fileRef}
        type="file"
        accept="image/*"
        onChange={onPickFile}
        disabled={uploading}
        className="hidden"
        data-testid={testId ? `${testId}-file-input` : undefined}
      />

      {/* PRIMARY: Upload dropzone / current-image preview */}
      {value ? (
        <div
          className="relative rounded-xl overflow-hidden"
          style={{ border: '1px solid rgba(212,175,55,0.3)', background: 'rgba(0,0,0,0.25)' }}
          data-testid={testId ? `${testId}-preview` : undefined}
        >
          <img
            src={value}
            alt="preview"
            className="w-full max-h-48 object-contain"
            onError={(e) => { e.target.style.display = 'none'; }}
          />
          <div className="flex items-center justify-between px-3 py-2" style={{ background: 'rgba(0,0,0,0.4)' }}>
            <span className="flex items-center gap-2 text-xs" style={{ color: '#22C55E' }}>
              <Check className="w-3.5 h-3.5" /> Uploaded
            </span>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={triggerPicker}
                disabled={uploading}
                className="text-xs px-3 py-1 rounded-full disabled:opacity-50"
                style={{ background: 'rgba(212,175,55,0.2)', color: '#D4AF37' }}
                data-testid={testId ? `${testId}-replace-btn` : undefined}
              >
                {uploading ? 'Uploading...' : 'Replace'}
              </button>
              <button
                type="button"
                onClick={clearImage}
                className="text-xs px-3 py-1 rounded-full flex items-center gap-1"
                style={{ background: 'rgba(239,68,68,0.2)', color: '#EF4444' }}
                data-testid={testId ? `${testId}-clear-btn` : undefined}
              >
                <X className="w-3 h-3" /> Remove
              </button>
            </div>
          </div>
        </div>
      ) : (
        <button
          type="button"
          onClick={triggerPicker}
          onDrop={onDrop}
          onDragOver={onDragOver}
          onDragLeave={onDragLeave}
          disabled={uploading}
          className="w-full flex flex-col items-center justify-center gap-2 py-8 px-4 rounded-xl transition-all disabled:opacity-60"
          style={{
            background: dragActive ? 'rgba(212,175,55,0.12)' : 'rgba(212,175,55,0.06)',
            border: `2px dashed ${dragActive ? '#D4AF37' : 'rgba(212,175,55,0.4)'}`,
            color: '#D4AF37',
            cursor: uploading ? 'wait' : 'pointer',
          }}
          data-testid={testId ? `${testId}-dropzone` : undefined}
        >
          {uploading ? (
            <>
              <Loader2 className="w-7 h-7 animate-spin" />
              <span className="text-sm font-medium">Uploading...</span>
            </>
          ) : (
            <>
              <Upload className="w-7 h-7" />
              <span className="text-sm font-medium">Tap to upload image</span>
              <span className="text-xs" style={{ color: '#A3B8AC' }}>
                Gallery / Camera / File Explorer · PNG / JPG / WEBP · max 20MB
              </span>
            </>
          )}
        </button>
      )}

      {/* Validation hidden input so native form `required` works */}
      {required && (
        <input
          type="text"
          value={value || ''}
          onChange={() => {}}
          required
          tabIndex={-1}
          aria-hidden="true"
          style={{ position: 'absolute', opacity: 0, height: 0, width: 0, pointerEvents: 'none' }}
        />
      )}

      {/* ADVANCED: manual URL paste (collapsed by default) */}
      <div>
        <button
          type="button"
          onClick={() => setShowAdvanced((v) => !v)}
          className="text-xs flex items-center gap-1.5 mt-1"
          style={{ color: '#A3B8AC' }}
          data-testid={testId ? `${testId}-advanced-toggle` : undefined}
        >
          <LinkIcon className="w-3 h-3" />
          {showAdvanced ? 'Hide advanced' : 'Advanced: paste image URL instead'}
        </button>
        {showAdvanced && (
          <input
            type="url"
            value={value || ''}
            onChange={(e) => onChange(e.target.value)}
            placeholder="https://..."
            className="mt-2 w-full rounded-lg px-3 py-2 text-sm outline-none"
            style={{ background: 'rgba(0,0,0,0.2)', border: '1px solid rgba(255,255,255,0.1)', color: '#F8FAF9' }}
            data-testid={testId ? `${testId}-url-input` : undefined}
          />
        )}
      </div>
    </div>
  );
};
